import { useContext, useMemo } from 'react';
import { ButtonProps } from '@chakra-ui/react';

import { useSummaryControlAction } from '~/core/shared/api/middle-office/summary/hooks/useSummaryControlAction';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { useToast } from '~/core/shared/lib/chakraUi/hooks';
import { DefinitionContext } from '../../../../../../../contexts';
import { EntityControlMenuItemProps } from '../../types/EntityControlMenuItemProps';

export const useEntityControlMenuItem = (
  control: EntityControlMenuItemProps['control'],
  documentId: EntityControlMenuItemProps['documentId'],
  handleSideEffects: EntityControlMenuItemProps['handleSideEffects'],
  onOpenConfirm: EntityControlMenuItemProps['onOpenConfirm'],
) => {
  const { definition } = useContext(DefinitionContext);
  const { triggerSummaryControlAction } = useSummaryControlAction(definition);
  const toast = useToast();

  const buttonProps = useMemo((): ButtonProps | undefined => {
    if (control.options?.theme === 'danger') {
      return {
        colorPalette: 'red',
        variant: 'solid',
      };
    }
    return undefined;
  }, [control.options?.theme]);

  const handleClick = useEventHandler(async (): Promise<void> => {
    const response = await triggerSummaryControlAction({
      controlId: control.id,
      documentId,
    });
    const data = response?.data;
    if (data) {
      toast({
        type: data.success ? 'success' : 'error',
        title: data.message,
      });
      handleSideEffects(data.sideEffects);
    }
  });

  const onClick = useEventHandler((): void => {
    const confirm = control.options?.confirm as string | undefined;
    if (confirm) {
      onOpenConfirm({
        title: control.name,
        message: confirm,
        onConfirm: handleClick,
        confirmButtonProps: buttonProps,
      });
    } else {
      handleClick();
    }
  });

  return { buttonProps, onClick };
};
